import {
  getConverationsClickedUsage,
  getCopiedConversationCount,
  getCopiedConversationCountAsBins,
  getModelUsage,
  getModelUsagePerDays,
  getNHQAUsage,
  getNHQAUsagePerDay,
  getPromptUsageEvents,
  getTagsUsageCount,
  getTemporaryChatUserUsage,
  getUploadedFilesCount,
  getUploadedFilesCountAsBins,
  getUserActivityWithinTimeframe,
  getUserActivityWithinTimeframeAsBins,
  getWorkspaceLibraryDocumentsAsBins,
  getWorkspaceLibraryDocumentsCount,
  getWorkspacesUsageAsBins,
  getWorkspacesUsageCount,
} from "api";
import { LLMType } from "models/enums/LLMType";
import { IUseCase } from "models/IUseCase";
import { useState } from "react";
import { designTokens } from "theme/customTheme";

interface IModelUsage {
  _id: string;
  count: number;
}

interface IBinData {
  x: Date;
  y: number;
}

interface IModelUsageBinData {
  legend: string;
  data: IBinData[];
  color?: string;
}

// Pinned so the series added after LLaMA never share a hue with the
// auto-assigned colours of the existing series (the chart assigns those by
// position).
const CLAUDE_SONNET_55_COLOR = designTokens.accent[500];
const CLAUDE_SONNET_55_WEB_SEARCH_COLOR = designTokens.primary[600];
const CLAUDE_HAIKU_55_COLOR = designTokens.semantic.success;
const CLAUDE_OPUS_48_COLOR = designTokens.semantic.warning;
const CLAUDE_OPUS_46_COLOR = designTokens.semantic.warningDark;
const CLAUDE_OPUS_47_COLOR = designTokens.primary[900];
const CLAUDE_SONNET_46_COLOR = designTokens.neutral[500];
const CLAUDE_OPUS_55_COLOR = designTokens.semantic.info;

export const useStatisticsData = (useCases: IUseCase[]) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userActivityCount, setUserActivityCount] = useState<
    number | undefined
  >(undefined);
  const [copiedConversationsCount, setCopiedConversationCount] = useState<
    number | undefined
  >(undefined);
  const [fileUploadCount, setFilesUploadCount] = useState<number | undefined>(
    undefined,
  );
  const [nhqaCount, setNHQACount] = useState<number | undefined>(undefined);
  const [modelUsageCounts, setModelUsageCounts] = useState<IModelUsage[]>([]);
  const [kcCounts, setKcCounts] = useState<IModelUsage[]>([]);
  const [tempChatCount, setTempChatCount] = useState<number | undefined>(
    undefined,
  );
  const [workspacesCount, setWorkspacesCount] = useState<number | undefined>(
    undefined,
  );
  const [conversationClicks, setConversationClicks] = useState<
    number | undefined
  >(undefined);
  const [groupedPromptUsage, setGroupedPromptUsage] = useState<
    {
      useCaseTitle: string;
      prompts: { promptTitle: string; count: number }[];
    }[]
  >([]);

  const [activityBinData, setActivityBinData] = useState<IBinData[]>([]);
  const [activityMin, setActivityMin] = useState<number>(0);
  const [activityMax, setActivityMax] = useState<number>(0);

  const [copiedConversationsBinData, setCopiedConversationsBinData] = useState<
    IBinData[]
  >([]);
  const [copiedConversationsMax, setCopiedConversationsMax] =
    useState<number>(0);

  const [fileUploadsBinData, setFileUploadsBinData] = useState<IBinData[]>([]);
  const [fileUploadsMax, setFileUploadsMax] = useState<number>(0);

  const [modelUsageBinData, setModelUsageBinData] = useState<
    IModelUsageBinData[]
  >([]);
  const [modelUsageMin, setModelUsageMin] = useState<number>(0);
  const [modelUsageMax, setModelUsageMax] = useState<number>(0);

  const [nhqaUsageBinData, setNHQAUsageBinData] = useState<IBinData[]>([]);
  const [modelNHQAMax, setNHQAMax] = useState<number>(0);

  const [workspacesMax, setWorkspacesMax] = useState<number>(0);
  const [workspacesUsageBinData, setWorkspacesUsageBinData] = useState<
    IBinData[]
  >([]);

  const [workspaceLibraryDocumentsCount, setWorkspaceLibraryDocumentsCount] =
    useState<number | undefined>(undefined);
  const [
    workspaceLibraryDocumentsBinData,
    setWorkspaceLibraryDocumentsBinData,
  ] = useState<IBinData[]>([]);
  const [workspaceLibraryDocumentsMax, setWorkspaceLibraryDocumentsMax] =
    useState<number>(0);

  const fetchAllStatistics = async (startDate: Date, endDate: Date) => {
    setIsLoading(true);
    try {
      const [
        activityResponse,
        activityBinResponse,
        copiedConversationsResponse,
        copiedConversationsBinResponse,
        uploadFilesBinResponse,
        fileUploads,
        nhqaResponse,
        nhqaBinResponse,
        modelUsageBinResponse,
        modelUsageResponse,
        tagsUsageResponse,
        tempChatResponse,
        workspacesResponse,
        conversationClickedCount,
        workspacesBinResponse,
        promptUsageEventsResponse,
        workspaceLibraryDocumentsResponse,
        workspaceLibraryDocumentsBinResponse,
        // mcpTotalUsageResponse,
        // mcpTotalUsageBinResponse,
        // mcpUsageSummaryResponse,
        // mcpUniqueUsersResponse,
      ] = await Promise.all([
        getUserActivityWithinTimeframe({ startDate, endDate }),
        getUserActivityWithinTimeframeAsBins({ startDate, endDate }),
        getCopiedConversationCount({ startDate, endDate }),
        getCopiedConversationCountAsBins({ startDate, endDate }),
        getUploadedFilesCountAsBins({ startDate, endDate }),
        getUploadedFilesCount({ startDate, endDate }),
        getNHQAUsage({ startDate, endDate }),
        getNHQAUsagePerDay({ startDate, endDate }),
        getModelUsagePerDays({ startDate, endDate }),
        getModelUsage({ startDate, endDate }),
        getTagsUsageCount({ startDate, endDate }),
        getTemporaryChatUserUsage({ startDate, endDate }),
        getWorkspacesUsageCount({ startDate, endDate }),
        getConverationsClickedUsage({ startDate, endDate }),
        getWorkspacesUsageAsBins({ startDate, endDate }),
        getPromptUsageEvents({ startDate, endDate }),
        getWorkspaceLibraryDocumentsCount({ startDate, endDate }),
        getWorkspaceLibraryDocumentsAsBins({ startDate, endDate }),
        // getMcpTotalUsage({ startDate, endDate }),
        // getMcpTotalUsageAsBins({ startDate, endDate }),
        // getMcpUsageSummary({ startDate, endDate }),
        // getMcpUniqueUsers({ startDate, endDate }),
      ]);

      const activityCount =
        Array.isArray(activityResponse) && activityResponse.length > 0
          ? activityResponse[0].uniqueUsers
          : 0;
      setUserActivityCount(activityCount);

      let minActivity = 0;
      let maxActivity = 0;
      const processedActivityBinData =
        activityBinResponse
          ?.filter((activity: any) => activity?._id)
          .map((activity: any) => {
            if (activity?.uniqueUserCount < minActivity)
              minActivity = activity?.uniqueUserCount;
            if (activity?.uniqueUserCount > maxActivity)
              maxActivity = activity?.uniqueUserCount;
            return {
              x: new Date(activity._id),
              y: activity?.uniqueUserCount,
            };
          }) || [];
      setActivityBinData(processedActivityBinData);
      setActivityMin(minActivity);
      setActivityMax(maxActivity);

      if (copiedConversationsResponse?.length > 0) {
        setCopiedConversationCount(
          copiedConversationsResponse[0]?.total_copied_conversations,
        );
      }

      let maxCopied = 0;
      const processedCopiedBinData =
        copiedConversationsBinResponse
          ?.filter((activity: any) => activity?._id)
          .map((activity: any) => {
            if (activity?.count > maxCopied) maxCopied = activity?.count;
            return {
              x: new Date(activity._id),
              y: activity?.count,
            };
          }) || [];
      setCopiedConversationsBinData(processedCopiedBinData);
      setCopiedConversationsMax(maxCopied);

      if (fileUploads?.length > 0) {
        setFilesUploadCount(fileUploads[0]?.total_uploaded_documents);
      }

      let maxUploads = 0;
      const processedUploadsBinData =
        uploadFilesBinResponse
          ?.filter((activity: any) => activity?._id)
          .map((activity: any) => {
            if (activity?.count > maxUploads) maxUploads = activity?.count;
            return {
              x: new Date(activity._id),
              y: activity?.count,
            };
          }) || [];
      setFileUploadsBinData(processedUploadsBinData);
      setFileUploadsMax(maxUploads);

      if (nhqaResponse?.length > 0) setNHQACount(nhqaResponse[0]?.total_rag);

      let maxNHQA = 0;
      const processedNHQABinData =
        nhqaBinResponse
          ?.filter((activity: any) => activity?._id)
          .map((activity: any) => {
            if (activity?.uniqueNHQAConversations > maxNHQA) {
              maxNHQA = activity?.uniqueNHQAConversations;
            }
            return {
              x: new Date(activity._id),
              y: activity?.uniqueNHQAConversations,
            };
          }) || [];
      setNHQAUsageBinData(processedNHQABinData);
      setNHQAMax(maxNHQA);

      const processedModelUsageBinData = processModelUsageBins(
        modelUsageBinResponse,
      );
      setModelUsageBinData(processedModelUsageBinData.data);
      setModelUsageMin(processedModelUsageBinData.minCount);
      setModelUsageMax(processedModelUsageBinData.maxCount);

      setModelUsageCounts(modelUsageResponse || []);

      setKcCounts(tagsUsageResponse || []);

      setTempChatCount(tempChatResponse || 0);

      if (workspacesResponse?.length > 0) {
        setWorkspacesCount(workspacesResponse[0]?.total_workspaces);
      }

      let maxWorkspaces = 0;
      const processedWorkspacesBinData =
        workspacesBinResponse
          ?.filter((activity: any) => activity?._id)
          .map((activity: any) => {
            if (activity?.count > maxWorkspaces)
              maxWorkspaces = activity?.count;
            return {
              x: new Date(activity._id),
              y: activity?.count,
            };
          }) || [];
      setWorkspacesUsageBinData(processedWorkspacesBinData);
      setWorkspacesMax(maxWorkspaces);

      setConversationClicks(conversationClickedCount || 0);

      if (workspaceLibraryDocumentsResponse?.length > 0) {
        setWorkspaceLibraryDocumentsCount(
          workspaceLibraryDocumentsResponse[0]?.total_library_documents,
        );
      }

      let maxLibraryDocs = 0;
      const processedLibraryDocsBinData =
        workspaceLibraryDocumentsBinResponse
          ?.filter((activity: any) => activity?._id)
          .map((activity: any) => {
            if (activity?.count > maxLibraryDocs)
              maxLibraryDocs = activity?.count;
            return {
              x: new Date(activity._id),
              y: activity?.count,
            };
          }) || [];
      setWorkspaceLibraryDocumentsBinData(processedLibraryDocsBinData);
      setWorkspaceLibraryDocumentsMax(maxLibraryDocs);

      const groupedUsageArray = processPromptUsageEvents(
        promptUsageEventsResponse,
        useCases,
      );
      setGroupedPromptUsage(groupedUsageArray);

      // setMcpTotalUsage(mcpTotalUsageResponse || 0);
      // setMcpUsageSummary(mcpUsageSummaryResponse || []);
      // setMcpUniqueUsers(mcpUniqueUsersResponse || 0);

      // let maxMcp = 0;
      // const processedMcpBinData =
      //   mcpTotalUsageBinResponse
      //     ?.filter((activity: any) => activity?._id)
      //     .map((activity: any) => {
      //       if (activity?.count > maxMcp) maxMcp = activity?.count;
      //       return {
      //         x: new Date(activity._id),
      //         y: activity?.count,
      //       };
      //     }) || [];
      // setMcpUsageBinData(processedMcpBinData);
      // setMcpUsageMax(maxMcp);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    userActivityCount,
    copiedConversationsCount,
    fileUploadCount,
    nhqaCount,
    modelUsageCounts,
    kgcounts: kcCounts,
    tempChatCount,
    workspacesCount,
    conversationClicks,
    groupedPromptUsage,
    activityBinData,
    activityMin,
    activityMax,
    copiedConversationsBinData,
    copiedConversationsMax,
    fileUploadsBinData,
    fileUploadsMax,
    modelUsageBinData,
    modelUsageMin,
    modelUsageMax,
    nhqaUsageBinData,
    modelNHQAMax,
    workspacesMax,
    workspacesUsageBinData,
    workspaceLibraryDocumentsCount,
    workspaceLibraryDocumentsBinData,
    workspaceLibraryDocumentsMax,
    mcpTotalUsage: undefined as number | undefined,
    mcpUniqueUsers: undefined as number | undefined,
    mcpUsageSummary: [] as { _id: string; count: number }[],
    mcpUsageBinData: [] as IBinData[],
    mcpUsageMax: 0,
    fetchAllStatistics,
  };
};

export const processModelUsageBins = (modelUsageBinResponse: any[]) => {
  if (!modelUsageBinResponse?.length) {
    return { data: [], minCount: 0, maxCount: 0 };
  }
  // Deprecated models — kept to display historical data
  const claudeUsage: IBinData[] = [];
  const claude37Usage: IBinData[] = [];
  const claudeSonnet4Usage: IBinData[] = [];
  const llamaUsage: IBinData[] = [];

  // Active models
  const openAIUsage: IBinData[] = [];
  const openAI41Usage: IBinData[] = [];
  const openai5MiniUsage: IBinData[] = [];
  const gpt52Usage: IBinData[] = [];
  const gpt52ChatUsage: IBinData[] = [];
  const claudeSonnet45Usage: IBinData[] = [];
  const claudeSonnet45LargeUsage: IBinData[] = [];
  const claudeSonnet45WebSearchUsage: IBinData[] = [];
  const claudeHaiku45Usage: IBinData[] = [];
  const claudeOpus45Usage: IBinData[] = [];
  const claudeSonnet55Usage: IBinData[] = [];
  const claudeSonnet55WebSearchUsage: IBinData[] = [];
  const claudeHaiku55Usage: IBinData[] = [];
  const claudeOpus48Usage: IBinData[] = [];
  const claudeOpus46Usage: IBinData[] = [];
  const claudeOpus47Usage: IBinData[] = [];
  const claudeSonnet46Usage: IBinData[] = [];
  const claudeOpus55Usage: IBinData[] = [];

  let maxCount = 0;
  let minCount = 10000;

  for (const modelUsage of modelUsageBinResponse) {
    if (!modelUsage?._id) continue;
    const x = new Date(modelUsage._id);
    const foundModels = {
      openai: false,
      openai41: false,
      claude: false,
      claude37: false,
      claudeSonnet4: false,
      llama: false,
      openai5Mini: false,
      gpt52: false,
      gpt52Chat: false,
      claudeSonnet45: false,
      claudeSonnet45Large: false,
      claudeSonnet45WebSearch: false,
      claudeHaiku45: false,
      claudeOpus45: false,
      claudeSonnet55: false,
      claudeSonnet55WebSearch: false,
      claudeHaiku55: false,
      claudeOpus48: false,
      claudeOpus46: false,
      claudeOpus47: false,
      claudeSonnet46: false,
      claudeOpus55: false,
    };

    for (const modelWithCount of modelUsage?.models ?? []) {
      if (modelWithCount?.count > maxCount) maxCount = modelWithCount?.count;
      if (modelWithCount?.count < minCount) minCount = modelWithCount?.count;

      const point = { x, y: modelWithCount?.count };

      switch (modelWithCount?.model) {
        case LLMType.OPENAI:
          openAIUsage.push(point);
          foundModels.openai = true;
          break;
        case LLMType.OPENAI_41:
          openAI41Usage.push(point);
          foundModels.openai41 = true;
          break;
        case LLMType.GPT_5_MINI:
          openai5MiniUsage.push(point);
          foundModels.openai5Mini = true;
          break;
        case LLMType.GPT_52:
          gpt52Usage.push(point);
          foundModels.gpt52 = true;
          break;
        case LLMType.GPT_52_CHAT:
          gpt52ChatUsage.push(point);
          foundModels.gpt52Chat = true;
          break;
        case LLMType.CLAUDE_37:
          claude37Usage.push(point);
          foundModels.claude37 = true;
          break;
        case LLMType.CLAUDE_SONNET_4:
          claudeSonnet4Usage.push(point);
          foundModels.claudeSonnet4 = true;
          break;
        case LLMType.CLAUDE_SONNET_4_5:
          claudeSonnet45Usage.push(point);
          foundModels.claudeSonnet45 = true;
          break;
        case LLMType.CLAUDE_SONNET_4_5_LARGE_CONTEXT:
          claudeSonnet45LargeUsage.push(point);
          foundModels.claudeSonnet45Large = true;
          break;
        case LLMType.CLAUDE_SONNET_4_5_WEB_SEARCH:
          claudeSonnet45WebSearchUsage.push(point);
          foundModels.claudeSonnet45WebSearch = true;
          break;
        case LLMType.CLAUDE_HAIKU_4_5:
          claudeHaiku45Usage.push(point);
          foundModels.claudeHaiku45 = true;
          break;
        case LLMType.CLAUDE_OPUS_4_5:
          claudeOpus45Usage.push(point);
          foundModels.claudeOpus45 = true;
          break;
        case LLMType.CLAUDE_SONNET_5_5:
          claudeSonnet55Usage.push(point);
          foundModels.claudeSonnet55 = true;
          break;
        case LLMType.CLAUDE_SONNET_5_5_WEB_SEARCH:
          claudeSonnet55WebSearchUsage.push(point);
          foundModels.claudeSonnet55WebSearch = true;
          break;
        case LLMType.CLAUDE_HAIKU_5_5:
          claudeHaiku55Usage.push(point);
          foundModels.claudeHaiku55 = true;
          break;
        case LLMType.CLAUDE_OPUS_4_8:
          claudeOpus48Usage.push(point);
          foundModels.claudeOpus48 = true;
          break;
        case LLMType.CLAUDE_OPUS_4_6:
          claudeOpus46Usage.push(point);
          foundModels.claudeOpus46 = true;
          break;
        case LLMType.CLAUDE_OPUS_4_7:
          claudeOpus47Usage.push(point);
          foundModels.claudeOpus47 = true;
          break;
        case LLMType.CLAUDE_SONNET_4_6:
          claudeSonnet46Usage.push(point);
          foundModels.claudeSonnet46 = true;
          break;
        case LLMType.CLAUDE_OPUS_5_5:
          claudeOpus55Usage.push(point);
          foundModels.claudeOpus55 = true;
          break;
        case LLMType.CLAUDE:
          claudeUsage.push(point);
          foundModels.claude = true;
          break;
        case LLMType.LLAMA:
          llamaUsage.push(point);
          foundModels.llama = true;
          break;
      }
    }

    const zeroPoint = { x, y: 0 };
    if (!foundModels.openai) openAIUsage.push(zeroPoint);
    if (!foundModels.openai41) openAI41Usage.push(zeroPoint);
    if (!foundModels.claude) claudeUsage.push(zeroPoint);
    if (!foundModels.claude37) claude37Usage.push(zeroPoint);
    if (!foundModels.claudeSonnet4) claudeSonnet4Usage.push(zeroPoint);
    if (!foundModels.llama) llamaUsage.push(zeroPoint);
    if (!foundModels.openai5Mini) openai5MiniUsage.push(zeroPoint);
    if (!foundModels.gpt52) gpt52Usage.push(zeroPoint);
    if (!foundModels.gpt52Chat) gpt52ChatUsage.push(zeroPoint);
    if (!foundModels.claudeSonnet45) claudeSonnet45Usage.push(zeroPoint);
    if (!foundModels.claudeSonnet45Large)
      claudeSonnet45LargeUsage.push(zeroPoint);
    if (!foundModels.claudeSonnet45WebSearch)
      claudeSonnet45WebSearchUsage.push(zeroPoint);
    if (!foundModels.claudeHaiku45) claudeHaiku45Usage.push(zeroPoint);
    if (!foundModels.claudeOpus45) claudeOpus45Usage.push(zeroPoint);
    if (!foundModels.claudeSonnet55) claudeSonnet55Usage.push(zeroPoint);
    if (!foundModels.claudeSonnet55WebSearch)
      claudeSonnet55WebSearchUsage.push(zeroPoint);
    if (!foundModels.claudeHaiku55) claudeHaiku55Usage.push(zeroPoint);
    if (!foundModels.claudeOpus48) claudeOpus48Usage.push(zeroPoint);
    if (!foundModels.claudeOpus46) claudeOpus46Usage.push(zeroPoint);
    if (!foundModels.claudeOpus47) claudeOpus47Usage.push(zeroPoint);
    if (!foundModels.claudeSonnet46) claudeSonnet46Usage.push(zeroPoint);
    if (!foundModels.claudeOpus55) claudeOpus55Usage.push(zeroPoint);
  }

  return {
    data: [
      { legend: "OpenAI", data: openAIUsage },
      { legend: "OpenAI 4.1", data: openAI41Usage },
      { legend: "OpenAI 5 Mini", data: openai5MiniUsage },
      { legend: "GPT-5.2", data: gpt52Usage },
      { legend: "GPT-5.2 Chat", data: gpt52ChatUsage },
      { legend: "Claude", data: claudeUsage },
      { legend: "Claude 3.7", data: claude37Usage },
      { legend: "Claude Sonnet 4", data: claudeSonnet4Usage },
      { legend: "Claude Sonnet 4.5", data: claudeSonnet45Usage },
      {
        legend: "Claude Sonnet 4.5 - Large Context",
        data: claudeSonnet45LargeUsage,
      },
      {
        legend: "Claude Sonnet 4.5 - Web Search",
        data: claudeSonnet45WebSearchUsage,
      },
      { legend: "Claude Haiku 4.5", data: claudeHaiku45Usage },
      { legend: "Claude Opus 4.5", data: claudeOpus45Usage },
      { legend: "LLaMA", data: llamaUsage },
      // Appended after LLaMA so no existing series changes position or colour.
      {
        legend: "Claude Sonnet 5.5",
        data: claudeSonnet55Usage,
        color: CLAUDE_SONNET_55_COLOR,
      },
      {
        legend: "Claude Sonnet 5.5 - Web Search",
        data: claudeSonnet55WebSearchUsage,
        color: CLAUDE_SONNET_55_WEB_SEARCH_COLOR,
      },
      {
        legend: "Claude Haiku 5.5",
        data: claudeHaiku55Usage,
        color: CLAUDE_HAIKU_55_COLOR,
      },
      {
        legend: "Claude Opus 4.8",
        data: claudeOpus48Usage,
        color: CLAUDE_OPUS_48_COLOR,
      },
      {
        legend: "Claude Opus 4.6",
        data: claudeOpus46Usage,
        color: CLAUDE_OPUS_46_COLOR,
      },
      {
        legend: "Claude Opus 4.7",
        data: claudeOpus47Usage,
        color: CLAUDE_OPUS_47_COLOR,
      },
      {
        legend: "Claude Sonnet 4.6",
        data: claudeSonnet46Usage,
        color: CLAUDE_SONNET_46_COLOR,
      },
      {
        legend: "Claude Opus 5.5",
        data: claudeOpus55Usage,
        color: CLAUDE_OPUS_55_COLOR,
      },
    ],
    minCount,
    maxCount,
  };
};

const processPromptUsageEvents = (
  promptUsageEventsResponse: any[],
  useCases: IUseCase[],
) => {
  const groupedPromptMap = new Map<string, Map<string, number>>();

  for (const event of promptUsageEventsResponse) {
    const useCaseId = event?.useCaseId;
    const promptTitle = event?.promptTitle;

    if (!useCaseId || !promptTitle) continue;

    const useCase = useCases.find((uc) => uc.id === useCaseId);
    if (!useCase) continue;

    const useCaseTitle = useCase.title;

    if (!groupedPromptMap.has(useCaseTitle)) {
      groupedPromptMap.set(useCaseTitle, new Map());
    }

    const promptsMap = groupedPromptMap.get(useCaseTitle)!;
    promptsMap.set(promptTitle, (promptsMap.get(promptTitle) || 0) + 1);
  }

  return Array.from(groupedPromptMap.entries()).map(
    ([useCaseTitle, promptsMap]) => ({
      useCaseTitle,
      prompts: Array.from(promptsMap.entries()).map(([promptTitle, count]) => ({
        promptTitle,
        count,
      })),
    }),
  );
};
